import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {I18nStringItem} from '@tj/tj-dmp-lib-api';

/** Langues d'une traduction, dans l'ordre d'affichage. La première sert de repli quand une langue manque. */
export const TRANSLATION_LANGS = ['FR', 'EN', 'IT', 'DE', 'NL', 'ES'] as const;

/**
 * Traductions d'un bloc : un tableau en lecture, une modale pour ajouter ou modifier une clé
 * (cf. docs/ui-design-guidelines.md §9.1). Rien n'est enregistré ici : la liste est rendue à l'écran hôte.
 */
@Component({
    selector: 'dmp-translation-manager',
    templateUrl: './translation-manager.component.html',
    styleUrls: ['./translation-manager.component.scss'],
    standalone: false
})
export class TranslationManagerComponent implements OnInit {

    readonly langs = TRANSLATION_LANGS;
    readonly fallbackLang = TRANSLATION_LANGS[0];

    @Input() translations: I18nStringItem[] = [];
    /** Bouton « Insérer dans le code » de chaque ligne. */
    @Input() copyButton = true;
    /** L'hôte peut porter lui-même le bouton d'ajout (en-tête de son bloc) et appeler `create()`. */
    @Input() showAddButton = true;

    @Output() translationsChange = new EventEmitter();
    @Output() onCopyClick = new EventEmitter();

    modalType: 'create' | 'edit' = null;
    submitted = false;

    actual: I18nStringItem;
    actualIndex: number = null;

    ngOnInit(): void {
        this.translations = this.translations ?? [];
    }

    get keyMissing(): boolean {
        return !this.actual?.key?.trim();
    }

    get keyTaken(): boolean {
        const key = this.actual?.key?.trim();
        return !!key && this.translations.some((t, i) => t.key === key && i !== this.actualIndex);
    }

    get fallbackMissing(): boolean {
        return !this.actual?.values[this.fallbackLang]?.trim();
    }

    create() {
        this.open('create', null, {
            key: '',
            values: this.langs.reduce((acc, curr) => ({...acc, [curr]: ''}), {})
        });
    }

    edit(element: I18nStringItem, index: number) {
        // Une copie : « Annuler » ou la croix l'abandonnent sans toucher à la ligne (§1.1).
        this.open('edit', index, {
            key: element.key,
            values: this.langs.reduce((acc, curr) => ({...acc, [curr]: element.values[curr] ?? ''}), {})
        });
    }

    remove(index: number) {
        this.translations.splice(index, 1);
        this.translationsChange.emit(this.translations);
    }

    save() {
        this.submitted = true;
        if (this.keyMissing || this.keyTaken || this.fallbackMissing) {
            return;
        }
        // Une langue laissée vide n'est pas enregistrée : à l'envoi, elle reprend alors le français.
        const values = Object.entries(this.actual.values)
            .filter(([, value]) => value?.trim())
            .reduce((acc, [lang, value]) => ({...acc, [lang]: value}), {});
        const translation: I18nStringItem = {key: this.actual.key.trim(), values};
        if (this.modalType === 'create') {
            this.translations.push(translation);
        } else {
            this.translations[this.actualIndex] = translation;
        }
        this.translationsChange.emit(this.translations);
        this.close();
    }

    close() {
        this.modalType = null;
        this.actual = null;
        this.actualIndex = null;
    }

    /** La clé telle qu'elle s'écrit dans le code. Passée en paramètre : des accolades dans le texte traduit
        seraient lues par ngx-translate comme un paramètre. */
    placeholder(key: string): string {
        return `{{${key?.trim() || 'cle'}}}`;
    }

    copy(key: string) {
        this.onCopyClick.emit(key);
    }

    private open(type: 'create' | 'edit', index: number, translation: I18nStringItem) {
        this.submitted = false;
        this.actualIndex = index;
        this.actual = translation;
        this.modalType = type;
    }
}
